import type { EditorPlugin } from '../../types/aha-editor';
import { Editor, Transforms } from 'slate';
import { isSelectionInList } from '../ListPlugin/utils';
import { isListItem } from '../ListPlugin/guards';
import ListWrapUtils from './ListWrapUtils';
import {
  UnorderedListComponentPlugin,
  OrderedListComponentPlugin,
  CheckboxListComponentPlugin,
} from './ListComponentsPlugin';
import Markdown from '../Markdown/Markdown';

const convertToListOfType = (type: 'ol_list' | 'ul_list' | 'checklist') => {
  return (editor: Editor): undefined => {
    ListWrapUtils.wrapInList(editor, { type });
    return undefined;
  };
};

const convertToChecklist = (editor: Editor, markdownData): undefined => {
  convertToListOfType('checklist')(editor);
  const { matchData } = markdownData;

  const checkedRegExp = /\[x\]/i;
  if (!matchData[0].match(checkedRegExp)) return;

  // The list was created with [x] or [X], check the list item that was just
  // added.
  const listItemEntry = Editor.above(editor, { match: isListItem });
  if (!listItemEntry) return;

  const [, listItemPath] = listItemEntry;
  Transforms.setNodes(editor, { list_checked: true }, { at: listItemPath });
};

const revertList = (editor: Editor) => ListWrapUtils.unwrapList(editor);
const isNotInList = (editor: Editor) => !isSelectionInList(editor);

export const UnorderedListMarkdownPlugin: EditorPlugin = {
  name: 'Unordered list markdown plugin',
  dependencies: [Markdown, UnorderedListComponentPlugin],
  markdown: [
    {
      type: 'block',
      shortcuts: [/^\*$/, /^-$/],
      triggerCharacters: [' '],
      convert: convertToListOfType('ul_list'),
      shiftTabRevert: true,
      ignoreUncaptured: false,
      ignoreSameCharacterString: false,
      revert: revertList,
      test: isNotInList,
    },
  ],
};

export const OrderedListMarkdownPlugin: EditorPlugin = {
  name: 'Ordered list markdown plugin',
  dependencies: [Markdown, OrderedListComponentPlugin],
  markdown: [
    {
      type: 'block',
      shortcut: /^1\.$/,
      triggerCharacters: [' '],
      convert: convertToListOfType('ol_list'),
      shiftTabRevert: true,
      ignoreUncaptured: false,
      ignoreSameCharacterString: false,
      revert: revertList,
      test: isNotInList,
    },
  ],
};
export const CheckboxListMarkdownPlugin: EditorPlugin = {
  name: 'Checkbox list markdown plugin',
  dependencies: [Markdown, CheckboxListComponentPlugin],
  markdown: [
    {
      type: 'block',
      triggerCharacters: [' '],
      shortcuts: [/\[\s?\]$/, /\[x\]$/i],
      convert: convertToChecklist,
      shiftTabRevert: true,
      ignoreUncaptured: false,
      ignoreSameCharacterString: false,
      revert: revertList,
      test: isNotInList,
    },
  ],
};

const ListMarkdownPlugins: EditorPlugin[] = [
  UnorderedListMarkdownPlugin,
  OrderedListMarkdownPlugin,
  CheckboxListMarkdownPlugin,
];

export default ListMarkdownPlugins;
